/**
 * Text that is off-screen for sighted users and PRESENT for screen readers
 * (#1069 P3.2).
 *
 * ⚠️ THERE WAS NO SUCH HELPER IN THIS CODEBASE. `visuallyHidden`, `srOnly` and
 * `visually-hidden` all return nothing (measured 2026-08-25), so this is new
 * rather than a re-export.
 *
 * ⚠️ WHY THE UNREAD DOT NEEDS IT, AND WHY THE OBVIOUS ALTERNATIVE IS WORSE.
 * A dot announces nothing, so each level owes an explicit spoken state. The
 * naive fix — put an `accessibilityLabel` on the row — is actively harmful in
 * the two places this feature touches, because NEITHER of those rows has a
 * label today:
 *
 *   · `ViewRequests`' request row relies entirely on its concatenated children,
 *     so setting a label would SUPPRESS customer, project, region, dates, stage
 *     and requestor in one go (iOS collapses an accessible ancestor's
 *     children).
 *   · `CorrespondencePanel`'s thread row relies on title + sender-prefixed
 *     preview + relative timestamp — and the PREVIEW is the very thing PRD
 *     FR55's rationale cites as the reason the message count could be
 *     withdrawn. Hiding it from screen-reader users would invalidate that
 *     rationale.
 *
 * Rendering a hidden child ADDS state without REPLACING content, and it cannot
 * fall out of sync with the children as they change — which a hand-composed
 * label string would.
 *
 * ⚠️ THE STYLE IS THE CONTRACT, AND THREE TEMPTING ALTERNATIVES ARE WRONG:
 *
 *   · `display: 'none'` removes the node from the accessibility tree entirely.
 *   · `opacity: 0` is honoured by VoiceOver on native and the text is skipped.
 *   · `accessibilityElementsHidden` / `importantForAccessibility="no-*"` do
 *     exactly what this component must not do.
 *
 * The clip-to-1x1-and-move-off-screen construction below is the standard
 * `sr-only` recipe, is honoured by react-native-web (it emits a real DOM node
 * with these styles) and leaves the node in the tree on iOS and Android.
 * `position: 'absolute'` also keeps it out of flow, so it can be the first
 * child of a row without moving anything.
 *
 * @see GitLab #1069 — Request Correspondence
 */

import React from 'react';
import { StyleSheet, Text } from 'react-native';

interface Props {
  children: React.ReactNode;
  testID?: string;
}

export const VisuallyHiddenText: React.FC<Props> = ({ children, testID }) => (
  <Text style={styles.hidden} testID={testID}>
    {children}
  </Text>
);

const styles = StyleSheet.create({
  hidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    // Off-screen rather than merely clipped, so a platform that ignores
    // `overflow` on text still does not paint it over the row.
    left: -10000,
    top: 0,
    overflow: 'hidden',
    // Not zero: a zero font size is treated as "no text" by some engines.
    fontSize: 1,
    // No `opacity` and no `color: 'transparent'` — both are read as "hidden"
    // by at least one screen reader, which is the failure this exists to avoid.
  },
});
